import styled from '@emotion/styled';
import MicrositeResponsiveImg from '~/components/MicrositeResponsiveImg';
import type { MicrositeResponsiveImage } from '~/model';
import { LoadPriority } from '~/hooks/useLoadPriority';
import { css_background, css_fill, css_gap, css_medium_up, css_quick, css_text } from '~/emotion/theme';
import MicrositeSection from '~/components/MicrositeSection';
import useMicrositeState, { MicrositeStateActionType } from '~/hooks/useMicrositeState';
import BetaSectionHeader from '~/editor/templates/beta/lib/BetaSectionHeader';

const FEATURED_COUNT = 5;

const Mosaic = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${css_gap('grid')};

	@media (${css_medium_up}) {
		grid-template-columns: repeat(12, minmax(0, 1fr));
		grid-auto-rows: minmax(11rem, 15vw);
	}
`;

const Tile = styled.a<{ layout: 'hero' | 'tall' | 'standard' }>`
	display: block;
	overflow: hidden;
	background: ${css_fill()};

	@media (${css_medium_up}) {
		grid-column: ${({ layout }) => {
			if (layout === 'hero') return 'span 8';
			if (layout === 'tall') return 'span 4';
			return 'span 4';
		}};
		grid-row: ${({ layout }) => (layout === 'standard' ? 'span 1' : 'span 2')};
	}

	img {
		transition: transform ${css_quick};
	}

	&:hover img {
		transform: scale(1.04);
	}
`;

const GalleryImage = styled(MicrositeResponsiveImg)`
	width: 100%;
	height: 100%;
	object-fit: cover;
	aspect-ratio: 4 / 3;

	@media (${css_medium_up}) {
		aspect-ratio: auto;
	}
`;

const Footer = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 3rem;
`;

const Toggle = styled.button`
	display: inline-flex;
	align-items: center;
	gap: ${css_gap()};
	border: 1px solid ${css_text()};
	background: transparent;
	padding: 1rem 2rem;
	color: ${css_text()};
	text-transform: uppercase;
	letter-spacing: 0.3em;
	font-size: 0.75rem;
	font-weight: 700;
	transition:
		background ${css_quick},
		color ${css_quick};

	&:hover {
		background: ${css_text()};
		color: ${css_background()};
	}
`;

const ToggleIcon = styled.span`
	font-size: 1rem;
	line-height: 1;
`;

function FeaturedGallery({ photos }: { photos: MicrositeResponsiveImage[] }) {
	const featured = photos.slice(0, FEATURED_COUNT);
	const layouts: Array<'hero' | 'standard' | 'tall' | 'standard' | 'standard'> = ['hero', 'standard', 'tall', 'standard', 'standard'];

	return (
		<Mosaic>
			{featured.map((photo, index) => (
				<Tile key={index} href="#gallery" data-index={index} layout={layouts[index] ?? 'standard'}>
					<GalleryImage image={photo} priority={LoadPriority.FIRST_PAINT} />
				</Tile>
			))}
		</Mosaic>
	);
}

export default function GalleryBlockBeta({ photos, id }: { id: string; photos: MicrositeResponsiveImage[] }) {
	const { dispatch } = useMicrositeState();

	return (
		<MicrositeSection id={id} padding>
			<BetaSectionHeader align="end" title="Photos" titleFirst />

			<FeaturedGallery photos={photos} />

			{photos.length > FEATURED_COUNT && (
				<Footer>
					<Toggle type="button" onClick={() => dispatch({ type: MicrositeStateActionType.GALLERY_OPEN })}>
						<ToggleIcon>+</ToggleIcon>
						View {photos.length} Photos
					</Toggle>
				</Footer>
			)}
		</MicrositeSection>
	);
}
